import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Download, X, FileText, Image, File, Calendar, User, Tag } from "lucide-react";
import { format } from "date-fns";

interface Document {
  id: string;
  name: string;
  category: string;
  file_url: string;
  file_size: number | null;
  mime_type: string | null;
  expires_at: string | null;
  is_required: boolean | null;
  created_at: string;
  uploaded_by: string | null;
}

interface DocumentPreviewModalProps {
  document: Document | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  document,
  open,
  onOpenChange
}) => {
  const [downloading, setDownloading] = useState(false);

  if (!document) return null;

  const getFileIcon = () => {
    if (document.mime_type?.startsWith('image/')) {
      return <Image className="h-6 w-6 text-blue-500" />;
    }
    if (document.mime_type === 'application/pdf') {
      return <FileText className="h-6 w-6 text-red-500" />;
    }
    return <File className="h-6 w-6 text-gray-500" />;
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      contract: 'bg-blue-100 text-blue-800',
      identification: 'bg-green-100 text-green-800',
      certification: 'bg-purple-100 text-purple-800',
      tax_documents: 'bg-orange-100 text-orange-800',
      emergency_contact: 'bg-red-100 text-red-800',
      training: 'bg-yellow-100 text-yellow-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return 'Unknown size';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const formatCategory = (category: string) => {
    return category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expireDate = new Date(expiresAt);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expireDate <= thirtyDaysFromNow;
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      
      // Get a signed URL for secure download
      const fileName = document.file_url.split('/').pop();
      if (!fileName) throw new Error('Invalid file URL');

      const { data, error } = await supabase.storage
        .from('employee-documents')
        .createSignedUrl(fileName, 3600); // 1 hour expiry

      if (error) throw error;

      // Create a temporary link and trigger download
      const linkElement = window.document.createElement('a');
      linkElement.href = data.signedUrl;
      linkElement.download = document.name;
      window.document.body.appendChild(linkElement);
      linkElement.click();
      window.document.body.removeChild(linkElement);

      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloading(false);
    }
  };

  const canPreview = document.mime_type?.startsWith('image/') || document.mime_type === 'application/pdf';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {getFileIcon()}
              <div>
                <DialogTitle className="text-xl">{document.name}</DialogTitle>
                <Badge 
                  className={`mt-1 ${getCategoryColor(document.category)}`}
                  variant="secondary"
                >
                  {formatCategory(document.category)}
                </Badge>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Document Metadata */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Uploaded</p>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(document.created_at), 'MMM dd, yyyy')}
                  </p>
                </div>
              </div>

              {document.expires_at && (
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Expires</p>
                    <p className={`text-sm ${
                      isExpiringSoon(document.expires_at) 
                        ? 'text-orange-600 font-medium' 
                        : 'text-muted-foreground'
                    }`}>
                      {format(new Date(document.expires_at), 'MMM dd, yyyy')}
                      {isExpiringSoon(document.expires_at) && ' (Soon)'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <File className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">File Size</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(document.file_size)}
                  </p>
                </div>
              </div>

              {document.is_required && (
                <div className="flex items-center space-x-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Status</p>
                    <Badge variant="outline" className="text-xs">
                      Required Document
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* File Preview */}
          {canPreview && (
            <div className="space-y-3">
              <h3 className="text-lg font-medium">Preview</h3>
              <div className="border rounded-lg p-4 bg-gray-50">
                {document.mime_type?.startsWith('image/') ? (
                  <img
                    src={document.file_url}
                    alt={document.name}
                    className="max-w-full h-auto rounded-lg"
                  />
                ) : document.mime_type === 'application/pdf' ? (
                  <div className="text-center py-8">
                    <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-muted-foreground">
                      PDF preview not available. Click download to view the document.
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {document.mime_type || 'Unknown file type'}
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={handleDownload}
                disabled={downloading}
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>{downloading ? 'Downloading...' : 'Download'}</span>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};